import React, { useCallback, useMemo } from "react";
import { get } from "lodash";
import classNames from "classnames";
import { useForm, useFormState } from "react-final-form";
import { useSelector } from "react-redux";
import { useParams } from "react-router-dom";
import Input from "rs-components/Input";
import SelectInput from "rs-components/Input/components/SelectInput";
import SelectItemRenderer from "rs-components/Input/components/SelectInput/components/SelectItemRenderer";
import NumberInput from "rs-components/Input/components/NumberInput";
import TimingFields from "rs-components/TimingFields";
import RadioInputGroup from "components/Controls/RadioInputGroup";
import { CustomVariableFormFields } from "../CustomVariableDialog";
import { CutType, TimestampType } from "shared/types";
import { customVariableTransformationOptionsSelector, flatStepsSelector, } from "store/recipe/recipe.selectors";
import { isEditableSelector } from "store/knowledge-base-item/knowledge-base-item.selectors";
import { CustomVariableTransformationTimestampHumanType } from "store/recipe/recipe.types";
import { customVariableTimestampTypes, timeUnits } from "shared/rsDictionary";
import { getFieldRestrictions, getRequestName } from "store/knowledge-base-item/knowledge-base-item.utils";
import { CURRENT_TIME_TRANSFORMATION } from "store/recipe/custom-variables.utils";
import { getRecipeTimestamps } from "store/recipe/recipe.utils";
import * as styles from "./styles.module.scss";
const ValueTransformations = ({ valueFieldName, restrictions }) => {
    const baseFieldName = `${CustomVariableFormFields.formula}.${valueFieldName}`;
    const { id: recipeId } = useParams();
    const form = useForm();
    const formState = useFormState();
    const editable = useSelector(isEditableSelector);
    const transformationOptions = useSelector(customVariableTransformationOptionsSelector);
    const recipeSteps = useSelector(flatStepsSelector);
    const timestampOptions = useMemo(() => getRecipeTimestamps(recipeSteps), [recipeSteps]);
    const formulaType = useMemo(() => get(formState.values, `${CustomVariableFormFields.formula}.${CustomVariableFormFields.type}`), [formState.values]);
    const isPointInTimeTransformation = useMemo(() => {
        const transformationType = get(formState.values, `${baseFieldName}.${CustomVariableFormFields.transformationType}`);
        return transformationType === CutType.PointInTime;
    }, [formState.values, baseFieldName]);
    const isCurrentTimeTransformation = useMemo(() => {
        const transformationType = get(formState.values, `${baseFieldName}.${CustomVariableFormFields.transformationType}`);
        return transformationType === CURRENT_TIME_TRANSFORMATION;
    }, [formState.values, baseFieldName]);
    const onTimestampTypeChange = useCallback((value, cutFieldName, label) => {
        let timestampTypeFieldValue;
        if (value === CustomVariableTransformationTimestampHumanType.CurrentTime) {
            timestampTypeFieldValue = value;
        }
        if (value === CustomVariableTransformationTimestampHumanType.Timestamp) {
            if (isPointInTimeTransformation) {
                timestampTypeFieldValue = undefined;
            }
            else {
                timestampTypeFieldValue = label === "End" ? TimestampType.End : TimestampType.Start;
            }
        }
        form.change(`${baseFieldName}.${CustomVariableFormFields.cut}.${cutFieldName}`, {
            [CustomVariableFormFields._timestampType]: value,
            [CustomVariableFormFields.timestampType]: timestampTypeFieldValue,
        });
    }, [form, baseFieldName, isPointInTimeTransformation]);
    const onTransformationChange = useCallback((value) => {
        const cutTypeFormFieldName = `${baseFieldName}.${CustomVariableFormFields.cut}.${CustomVariableFormFields.type}`;
        form.batch(() => {
            form.change(cutTypeFormFieldName, value === CutType.PointInTime ? CutType.PointInTime : CutType.TimeSeries);
            if (value === TimestampType.CurrentTime) {
                form.change(`${baseFieldName}.${CustomVariableFormFields.cut}`, null);
                return;
            }
            if (value === CutType.PointInTime) {
                form.change(`${baseFieldName}.${CustomVariableFormFields.cut}.${CustomVariableFormFields.point}.${CustomVariableFormFields.timestampType}`, TimestampType.CurrentTime);
                form.change(`${baseFieldName}.${CustomVariableFormFields.cut}.${CustomVariableFormFields.point}.${CustomVariableFormFields._timestampType}`, CustomVariableTransformationTimestampHumanType.CurrentTime);
            }
            else {
                form.change(`${baseFieldName}.${CustomVariableFormFields.cut}.${CustomVariableFormFields.start}.${CustomVariableFormFields.timestampType}`, TimestampType.CurrentTime);
                form.change(`${baseFieldName}.${CustomVariableFormFields.cut}.${CustomVariableFormFields.start}.${CustomVariableFormFields._timestampType}`, CustomVariableTransformationTimestampHumanType.CurrentTime);
                form.change(`${baseFieldName}.${CustomVariableFormFields.cut}.${CustomVariableFormFields.end}.${CustomVariableFormFields.timestampType}`, TimestampType.CurrentTime);
                form.change(`${baseFieldName}.${CustomVariableFormFields.cut}.${CustomVariableFormFields.end}.${CustomVariableFormFields._timestampType}`, CustomVariableTransformationTimestampHumanType.CurrentTime);
            }
        });
    }, [baseFieldName, form]);
    const renderTimeConfigurationField = useCallback((label, cutFieldName, values) => {
        const currentTimeOffsetValue = get(formState.values, `${baseFieldName}.${CustomVariableFormFields.cut}.${cutFieldName}.${CustomVariableFormFields.offset}`);
        const timestampType = get(formState.values, `${baseFieldName}.${CustomVariableFormFields.cut}.${cutFieldName}.${CustomVariableFormFields.timestampType}`) === CustomVariableTransformationTimestampHumanType.CurrentTime
            ? CustomVariableTransformationTimestampHumanType.CurrentTime
            : CustomVariableTransformationTimestampHumanType.Timestamp;
        return (React.createElement("div", { className: styles.timeConfiguration },
            React.createElement("h3", { className: styles.label }, label),
            React.createElement(Input, { component: RadioInputGroup, disableFormChange: true, name: `${baseFieldName}.${CustomVariableFormFields.cut}.${cutFieldName}.${CustomVariableFormFields._timestampType}`, items: customVariableTimestampTypes, onChange: (value) => onTimestampTypeChange(value, cutFieldName, label), classes: { container: styles.toggle }, value: timestampType, readonly: !editable }),
            React.createElement("div", { className: styles.fields }, timestampType === CustomVariableTransformationTimestampHumanType.CurrentTime ? (React.createElement("div", { className: styles.offset },
                React.createElement(Input, { component: NumberInput, label: "Offset", name: `${baseFieldName}.${CustomVariableFormFields.cut}.${cutFieldName}.${CustomVariableFormFields.offset}`, restrictions: {
                        IsNumber: true,
                        ...getFieldRestrictions(restrictions, [
                            getRequestName("Formula"),
                            formulaType,
                            valueFieldName,
                            "Relative",
                            CustomVariableFormFields.cut,
                            "PointInTime",
                            CustomVariableFormFields.point,
                            CustomVariableFormFields.offset,
                        ]),
                    }, classes: { label: styles["input-label"], container: styles["timestamp-offset"] }, prefix: currentTimeOffsetValue === 0 ? "-" : undefined, valueFormatter: (value) => {
                        const base = `-${`${value}`.replaceAll("-", "")}`;
                        return isNaN(Number(base)) ? `${base}` : Number(base);
                    }, readonly: !editable }),
                React.createElement("div", { className: styles["timestamp-units"] },
                    React.createElement("p", { className: classNames(styles["input-label"], styles.extraMargin) }, "\u200B"),
                    React.createElement(Input, { component: SelectInput, itemRenderer: SelectItemRenderer, placeholder: "Select", name: `${baseFieldName}.${CustomVariableFormFields.cut}.${cutFieldName}.${CustomVariableFormFields.offsetUnit}`, items: timeUnits, valueFormatter: ({ value }) => value, restrictions: getFieldRestrictions(restrictions, [
                            getRequestName("Formula"),
                            formulaType,
                            valueFieldName,
                            "Relative",
                            CustomVariableFormFields.cut,
                            "PointInTime",
                            CustomVariableFormFields.point,
                            CustomVariableFormFields.offsetUnit,
                        ]), classes: { component: styles.select }, readonly: !editable })))) : (React.createElement("div", { className: styles.timestamp },
                React.createElement(TimingFields, { name: `${baseFieldName}.${CustomVariableFormFields.cut}.${cutFieldName}`, namePrefix: `${baseFieldName}.${CustomVariableFormFields.cut}.`, values: values, additionalTimestampOptions: timestampOptions, restrictions: getFieldRestrictions(restrictions, [
                        getRequestName("Formula"),
                        formulaType,
                        valueFieldName,
                        "Relative",
                        CustomVariableFormFields.cut,
                        isPointInTimeTransformation ? "PointInTime" : "TimeSeries",
                    ]), recipeIds: [parseInt(recipeId)] }))))));
    }, [
        timestampOptions,
        onTimestampTypeChange,
        baseFieldName,
        formState.values,
        formulaType,
        restrictions,
        valueFieldName,
        isPointInTimeTransformation,
        editable,
        recipeId,
    ]);
    const timeConfigurationSection = useMemo(() => {
        if (isPointInTimeTransformation) {
            const cutValues = get(formState.values, `${baseFieldName}.${CustomVariableFormFields.cut}.${CustomVariableFormFields.point}`);
            return renderTimeConfigurationField("Point in time", CustomVariableFormFields.point, cutValues);
        }
        else {
            const cutValuesStart = get(formState.values, `${baseFieldName}.${CustomVariableFormFields.cut}.${CustomVariableFormFields.start}`);
            const cutValuesEnd = get(formState.values, `${baseFieldName}.${CustomVariableFormFields.cut}.${CustomVariableFormFields.end}`);
            return (React.createElement(React.Fragment, null,
                renderTimeConfigurationField("Start", CustomVariableFormFields.start, cutValuesStart),
                renderTimeConfigurationField("End", CustomVariableFormFields.end, cutValuesEnd)));
        }
    }, [renderTimeConfigurationField, isPointInTimeTransformation, formState.values, baseFieldName]);
    return (React.createElement("div", { className: styles.root },
        React.createElement("div", { className: styles.top },
            React.createElement(Input, { name: `${baseFieldName}.${CustomVariableFormFields.transformationType}`, component: SelectInput, items: transformationOptions, itemRenderer: SelectItemRenderer, valueFormatter: ({ value }) => value, classes: { component: styles.select }, onChange: onTransformationChange, readonly: !editable })),
        isCurrentTimeTransformation ? null : (React.createElement("div", { className: styles.transformationFields }, timeConfigurationSection))));
};
export default ValueTransformations;
